import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { Trans } from "@lingui/react/macro";
import { useEffect, useRef } from "react";

import {
  type Effect,
  EFFECTS,
  usePokeWithPicture,
} from "~/hook/overlay/usePokeWithPicture.ts";
import { createWebDraggableSheet } from "~/ui/base/WebDraggableSheet.tsx";

const CanvasPreview = ({
  effect,
  frames,
}: {
  effect: Effect;
  frames: ImageBitmap[];
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || frames.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const first = frames[0];
    canvas.width = first.width;
    canvas.height = first.height;

    if (effect === "still") {
      const mid = Math.floor(frames.length / 2);
      ctx.drawImage(frames[mid], 0, 0);
      return;
    }

    const bounceFrames = [...frames, ...frames.slice().reverse()];
    const interval = 1000 / 15;
    let idx = 0;
    let last = performance.now();

    const draw = (now: number) => {
      if (now - last >= interval) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bounceFrames[idx % bounceFrames.length], 0, 0);
        idx++;
        last = now;
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [frames, effect]);

  return <canvas className="size-full object-cover" ref={canvasRef} />;
};

export const WebPokeWithPictureSheet = createWebDraggableSheet<{
  email: string;
}>(({ close, context: { email }, titleId }) => {
  const {
    cameraReady,
    captured,
    disabled,
    effect,
    facingMode,
    frames,
    handleCapture,
    handleRetake,
    handleSend,
    handleSwitchCamera,
    hasMultipleCameras,
    isCapturing,
    setEffect,
    videoRef,
  } = usePokeWithPicture(email, close);

  return (
    <div data-testid="사진 찌르기">
      <h1
        className="text-lg font-semibold text-zinc-800 dark:text-zinc-100"
        id={titleId}
      >
        <Trans>사진 찌르기</Trans> 📸
      </h1>
      <div className="h-6" />

      <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-black">
        {!captured && (
          <>
            <video
              className={`size-full object-cover ${facingMode === "user" ? "-scale-x-100" : ""}`}
              muted
              playsInline
              ref={videoRef}
            />
            {hasMultipleCameras && !isCapturing && cameraReady && (
              <button
                aria-label="Switch camera"
                className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md transition-all hover:bg-black/60 active:scale-95"
                onClick={handleSwitchCamera}
                type="button"
              >
                <ArrowPathIcon className="size-5" />
              </button>
            )}
            {isCapturing && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="size-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
              </div>
            )}
            {!cameraReady && !isCapturing && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="size-8 animate-spin rounded-full border-4 border-zinc-500 border-t-white dark:border-zinc-600" />
              </div>
            )}
          </>
        )}
        {captured && frames && (
          <CanvasPreview effect={effect} frames={frames} />
        )}
      </div>

      {captured && (
        <div className="mt-4 flex justify-center gap-3">
          {EFFECTS.map((e) => (
            <button
              className={`rounded-full px-4 py-2 text-sm font-medium duration-200 ${
                effect === e.value
                  ? "bg-black text-white dark:bg-zinc-800"
                  : "dark:bg-zinc-850 bg-zinc-100 text-zinc-700 dark:text-zinc-300"
              }`}
              key={e.value}
              onClick={() => {
                setEffect(e.value);
              }}
              type="button"
            >
              {e.icon} {e.label}
            </button>
          ))}
        </div>
      )}

      <div className="pt-6">
        {!captured && (
          <button
            className="dark:active:bg-zinc-750 h-12 w-full rounded-2xl bg-zinc-100 font-medium text-zinc-900 duration-300 active:bg-zinc-200 disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-100"
            disabled={!cameraReady || isCapturing}
            onClick={handleCapture}
            type="button"
          >
            {isCapturing ? (
              <Trans>촬영 중...</Trans>
            ) : (
              <>
                <Trans>촬영하기</Trans> 📸
              </>
            )}
          </button>
        )}
        {captured && (
          <div className="flex gap-3">
            <button
              className="dark:active:bg-zinc-750 h-12 flex-1 rounded-2xl bg-zinc-100 font-medium text-zinc-900 duration-300 active:bg-zinc-200 disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-100"
              disabled={disabled}
              onClick={handleRetake}
              type="button"
            >
              <Trans>다시 촬영</Trans>
            </button>
            <button
              className="h-12 flex-1 rounded-2xl bg-black font-medium text-white duration-300 active:bg-zinc-300 disabled:bg-zinc-300 dark:bg-zinc-800 dark:active:bg-zinc-700 dark:disabled:bg-zinc-900"
              data-testid="찌르기 버튼"
              disabled={disabled}
              onClick={() => void handleSend()}
              type="button"
            >
              <Trans>찌르기</Trans> 👉
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
